import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { jobsService } from "../services/jobs";
import { ApiError } from "../services/api";
import AnalysisPanel from "../components/AnalysisPanel";
import DocumentEditor from "../components/DocumentEditor";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import MarkdownModal from "../components/MarkdownModal";
import { Skeleton } from "../components/ui/Skeleton";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";
import { useToast } from "../context/ToastContext";
import type { JobDetail, ApplicationStatus } from "../types";

export default function JobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();

  const { show: showToast } = useToast();
  const [job, setJob] = useState<JobDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [jdModal, setJdModal] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    loadJob();
  }, [jobId]);

  async function loadJob() {
    setLoading(true);
    try {
      const data = await jobsService.get(jobId!);
      setJob(data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        navigate("/dashboard");
      } else {
        showToast("error", "Failed to load job.");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleAnalyze() {
    if (!jobId) return;
    setAnalyzing(true);
    try {
      const result = await jobsService.analyze(jobId);
      setJob((prev) =>
        prev
          ? {
              ...prev,
              alignment_score: result.alignment_score,
              success_prob: result.success_prob,
              match_analysis: result.match_analysis,
            }
          : prev
      );
      showToast("success", "Analysis complete.");
    } catch (err) {
      showToast("error", err instanceof ApiError ? err.detail : "Analysis failed.");
    } finally {
      setAnalyzing(false);
    }
  }

  async function handleStatusChange(e: React.ChangeEvent<HTMLSelectElement>) {
    if (!jobId) return;
    const newStatus = e.target.value as ApplicationStatus;
    setStatusUpdating(true);
    try {
      const updated = await jobsService.update(jobId, { application_status: newStatus });
      setJob((prev) => (prev ? { ...prev, application_status: updated.application_status } : prev));
    } catch {
      showToast("error", "Failed to update status.");
    } finally {
      setStatusUpdating(false);
    }
  }

  async function handleDelete() {
    if (!jobId) return;
    try {
      await jobsService.delete(jobId);
      showToast("success", "Job deleted.");
      navigate("/dashboard");
    } catch {
      showToast("error", "Failed to delete job.");
      setPendingDelete(false);
    }
  }

  useKeyboardShortcuts({
    onSave: () => {}, // DocumentEditor handles its own Cmd+S save
    onEscape: () => setJdModal(false),
  });

  if (loading) return (
    <div className="max-w-[900px] mx-auto px-6 pt-6 pb-16 space-y-4">
      <Skeleton variant="heading" />
      <Skeleton variant="line" />
      <Skeleton variant="line" />
      <Skeleton variant="line" />
    </div>
  );
  if (!job) return null;

  return (
    <div className="max-w-[900px] mx-auto px-6 pt-6 pb-16">
      {/* Breadcrumb */}
      <div className="mb-4 text-sm text-slate-500 dark:text-slate-400">
        <Link to="/dashboard" className="text-accent no-underline hover:underline">Dashboard</Link>
        {" / "}
        <span>{job.company_name || "Unknown Company"} — {job.job_title || "Unnamed Role"}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-0 mb-1">
            {job.job_title || "Unnamed Role"}
          </h1>
          <div className="text-slate-500 dark:text-slate-400 text-base">
            {job.company_name || "Unknown Company"}
            {job.source_url && (
              <>
                {" · "}
                <a
                  href={job.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent no-underline text-sm hover:underline"
                >
                  View original posting ↗
                </a>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={job.application_status}
            onChange={handleStatusChange}
            disabled={statusUpdating}
            className="px-2.5 py-1.5 border border-slate-300 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 cursor-pointer focus:outline-none focus:ring-2 focus:ring-accent transition-colors duration-200 disabled:opacity-50"
          >
            <option value="saved">Saved</option>
            <option value="applied">Applied</option>
            <option value="interview">Interview</option>
            <option value="offer">Offer</option>
            <option value="rejected">Rejected</option>
          </select>
          {pendingDelete ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600 dark:text-slate-400">Delete this job?</span>
              <Button variant="danger" size="sm" onClick={handleDelete}>
                Yes, delete
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setPendingDelete(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button variant="danger" size="sm" onClick={() => setPendingDelete(true)}>
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Meta row */}
      <div className="flex gap-6 mb-6 text-sm text-slate-500 dark:text-slate-400 flex-wrap">
        {job.posting_date && <span>Posted: {job.posting_date}</span>}
        {job.deadline_date && (
          <span>
            Deadline: <strong className="text-error">{job.deadline_date}</strong>
          </span>
        )}
        {job.applied_date && <span>Applied: {job.applied_date}</span>}
        <Badge variant={job.application_status}>
          {job.application_status.charAt(0).toUpperCase() + job.application_status.slice(1)}
        </Badge>
      </div>

      {/* Job Description */}
      <div className="mb-6">
        <button
          onClick={() => setJdModal(true)}
          className="text-accent text-sm underline bg-transparent border-none cursor-pointer p-0 hover:text-accent-hover transition-colors duration-200"
        >
          View Job Description
        </button>
      </div>

      {/* Analysis */}
      <div className="mb-6">
        <AnalysisPanel job={job} onAnalyze={handleAnalyze} analyzing={analyzing} />
      </div>

      {/* Document editors */}
      <div className="mb-2">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 m-0 mb-4">Documents</h2>
      </div>
      <div className="flex flex-col gap-6">
        <DocumentEditor jobId={jobId!} docType="cv" label="Curriculum Vitae (CV)" />
        <DocumentEditor jobId={jobId!} docType="cl" label="Cover Letter" />
      </div>

      {/* JD Modal */}
      {jdModal && (
        <MarkdownModal
          title="Job Description"
          content={job.jd_content}
          onClose={() => setJdModal(false)}
        />
      )}
    </div>
  );
}
